import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import { PermissionContextType } from '../types';
import { permissionApi } from '../services/api';
import { message } from 'antd';
import { requestDeduplication } from '../utils/requestDeduplication';

// 创建权限上下文
const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

// 权限上下文Provider属性
interface PermissionProviderProps {
  children: ReactNode;
}

// 权限上下文Provider组件
export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  // 使用useRef避免状态变化导致函数重新创建
  const lastFetchTimeRef = useRef<number>(0);
  const isInitializedRef = useRef<boolean>(false);

  const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

  // 清空权限信息
  const clearPermissions = useCallback(() => {
    console.log('🧹 清空权限信息');
    setPermissions([]);
    setLoading(false);
    setError(null);
    lastFetchTimeRef.current = 0;
    isInitializedRef.current = false;
    // 清除所有请求去重缓存
    requestDeduplication.clear();
  }, []);

  // 获取用户权限
  const fetchPermissions = useCallback(async (force = false) => {
    try {
      const now = Date.now();
      
      // 如果不强制刷新且缓存未过期，则跳过
      if (!force && now - lastFetchTimeRef.current < CACHE_DURATION && permissions.length > 0) {
        console.log('🔄 使用权限缓存');
        return;
      }

      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('admin_token');
      if (!token) {
        clearPermissions();
        return;
      }

      console.log('🔍 开始获取用户权限...');
      
      // 使用请求去重，但强制刷新时清除缓存
      if (force) {
        requestDeduplication.clear();
      }
      
      const response = await requestDeduplication.request(
        'getMyPermissions',
        () => permissionApi.getMyPermissions()
      );
      
      setPermissions(response.data.permissions || []);
      lastFetchTimeRef.current = now;
      isInitializedRef.current = true;
      console.log('✅ 权限加载完成:', response.data.permissions?.length || 0, '个权限');
    } catch (err: any) {
      console.error('获取权限失败:', err);
      if (err.response?.status === 401) {
        // 未授权，清除token和权限
        localStorage.removeItem('admin_token');
        clearPermissions();
      } else {
        setError(err.response?.data?.message || '获取权限失败');
        message.error('获取权限失败');
      }
    } finally {
      setLoading(false);
    }
  }, [permissions.length, clearPermissions]);

  // 检查单个权限
  const hasPermission = useCallback((permission: string): boolean => {
    return permissions.includes(permission);
  }, [permissions]);

  // 检查任一权限（OR关系）
  const hasAnyPermission = useCallback((requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(permission => permissions.includes(permission));
  }, [permissions]);

  // 检查所有权限（AND关系）
  const hasAllPermissions = useCallback((requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(permission => permissions.includes(permission));
  }, [permissions]);

  // 刷新权限（强制刷新）
  const refreshPermissions = useCallback(async (): Promise<void> => {
    console.log('🔄 强制刷新权限');
    await fetchPermissions(true);
  }, [fetchPermissions]);

  // 组件挂载时获取权限（只执行一次）
  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (token && !isInitializedRef.current) {
      console.log('🔄 组件初始化，获取权限...');
      fetchPermissions(true); // 组件挂载时强制刷新
    } else if (!token) {
      setLoading(false);
    }
  }, []); // 空依赖数组，只在组件挂载时执行一次

  // 监听token变化，重新获取权限
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'admin_token') {
        if (e.newValue) {
          console.log('🔑 检测到token变化，重新获取权限');
          fetchPermissions(true); // token变化时强制刷新
        } else {
          console.log('🗑️ token被删除，清空权限');
          clearPermissions();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // 移除依赖项，使用稳定的函数引用

  const contextValue: PermissionContextType = useMemo(() => ({
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    loading,
    error,
    refreshPermissions,
    clearPermissions
  }), [permissions, loading, error, hasPermission, hasAnyPermission, hasAllPermissions, refreshPermissions, clearPermissions]);

  return (
    <PermissionContext.Provider value={contextValue}>
      {children}
    </PermissionContext.Provider>
  );
};

// 权限Hook
export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
};

// 权限检查Hook
export const usePermissionCheck = () => {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();
  
  // 使用useCallback缓存所有权限检查函数，确保引用稳定
  const canViewUsers = useCallback(() => hasPermission('user.view'), [hasPermission]);
  const canCreateUsers = useCallback(() => hasPermission('user.create'), [hasPermission]);
  const canEditUsers = useCallback(() => hasPermission('user.edit'), [hasPermission]);
  const canDeleteUsers = useCallback(() => hasPermission('user.delete'), [hasPermission]);
  const canViewRoles = useCallback(() => hasPermission('role.view'), [hasPermission]);
  const canCreateRoles = useCallback(() => hasPermission('role.create'), [hasPermission]);
  const canEditRoles = useCallback(() => hasPermission('role.edit'), [hasPermission]);
  const canDeleteRoles = useCallback(() => hasPermission('role.delete'), [hasPermission]);
  const canViewPermissions = useCallback(() => hasPermission('permission.view'), [hasPermission]);
  const canCreatePermissions = useCallback(() => hasPermission('permission.create'), [hasPermission]);
  const canEditPermissions = useCallback(() => hasPermission('permission.edit'), [hasPermission]);
  const canDeletePermissions = useCallback(() => hasPermission('permission.delete'), [hasPermission]);
  const canViewAPIs = useCallback(() => hasPermission('api.view'), [hasPermission]);
  const canCreateAPIs = useCallback(() => hasPermission('api.create'), [hasPermission]);
  const canEditAPIs = useCallback(() => hasPermission('api.edit'), [hasPermission]);
  const canDeleteAPIs = useCallback(() => hasPermission('api.delete'), [hasPermission]);
  const canRegenerateAPIs = useCallback(() => hasPermission('api.regenerate'), [hasPermission]);
  const canViewLogs = useCallback(() => hasPermission('log.view'), [hasPermission]);
  const canViewUserLogs = useCallback(() => hasPermission('user_log.view'), [hasPermission]);
  const canExportUserLogs = useCallback(() => hasPermission('user_log.export'), [hasPermission]);
  const canViewAPILogs = useCallback(() => hasPermission('api_log.view'), [hasPermission]);
  const canViewAllAPILogs = useCallback(() => hasPermission('api_log.view_all'), [hasPermission]);
  const canExportAPILogs = useCallback(() => hasPermission('api_log.export'), [hasPermission]);
  const canViewStats = useCallback(() => hasPermission('stats.view'), [hasPermission]);
  const canViewDashboardStats = useCallback(() => hasPermission('stats.dashboard'), [hasPermission]);
  const canViewUserRoles = useCallback(() => hasPermission('user_role.view'), [hasPermission]);
  const canAssignUserRoles = useCallback(() => hasPermission('user_role.assign'), [hasPermission]);
  const canBulkAssignRoles = useCallback(() => hasPermission('user_role.bulk_assign'), [hasPermission]);
  const canViewDashboard = useCallback(() => hasPermission('dashboard.view'), [hasPermission]);
  const canAssignRoles = useCallback(() => hasAnyPermission(['user.assign_role', 'user_role.assign']), [hasAnyPermission]);
  const canAssignPermissions = useCallback(() => hasPermission('role.assign_permission'), [hasPermission]);
  // 大模型管理权限检查
  const canViewModels = useCallback(() => hasPermission('model.view'), [hasPermission]);
  const canCreateModels = useCallback(() => hasPermission('model.create'), [hasPermission]);
  const canEditModels = useCallback(() => hasPermission('model.edit'), [hasPermission]);
  const canDeleteModels = useCallback(() => hasPermission('model.delete'), [hasPermission]);
  const canToggleModels = useCallback(() => hasPermission('model.toggle'), [hasPermission]);
  const canViewModelStats = useCallback(() => hasPermission('model.stats'), [hasPermission]);
  const canBatchOperateModels = useCallback(() => hasPermission('model.batch'), [hasPermission]);
  // 偏好设置权限检查
  const canViewPreferences = useCallback(() => hasPermission('preference.view'), [hasPermission]);
  const canEditPreferences = useCallback(() => hasPermission('preference.edit'), [hasPermission]);
  const canViewPreferenceStats = useCallback(() => hasPermission('preference.stats'), [hasPermission]);
  const canResetPreferences = useCallback(() => hasPermission('preference.reset'), [hasPermission]);
  // API测试权限检查
  const canViewApiTest = useCallback(() => hasPermission('api_test.view'), [hasPermission]);
  const canViewApiTestBalance = useCallback(() => hasPermission('api_test.view_balance'), [hasPermission]);
  const canViewApiTestModels = useCallback(() => hasPermission('api_test.view_models'), [hasPermission]);
  const canManageApiTestKey = useCallback(() => hasPermission('api_test.manage_apikey'), [hasPermission]);
  const canGenerateText = useCallback(() => hasPermission('api_test.generate_text'), [hasPermission]);
  const canSetDefaultModel = useCallback(() => hasPermission('api_test.set_default_model'), [hasPermission]);
  // 充值管理权限检查
  const canViewRecharge = useCallback(() => hasPermission('recharge.view'), [hasPermission]);
  // 新增：查看充值订单权限
  const canViewRechargeOrders = useCallback(() => hasPermission('recharge.order.view'), [hasPermission]);
  // 聊天功能权限检查
  const canViewChat = useCallback(() => hasPermission('chat.view'), [hasPermission]);
  const canCreateChat = useCallback(() => hasPermission('chat.create_session'), [hasPermission]);
  const canEditChat = useCallback(() => hasPermission('chat.edit_session'), [hasPermission]);
  const canDeleteChat = useCallback(() => hasPermission('chat.delete_session'), [hasPermission]);
  const canSendMessage = useCallback(() => hasPermission('chat.send_message'), [hasPermission]);
  const canViewChatHistory = useCallback(() => hasPermission('chat.view_history'), [hasPermission]);
  const canSwitchChatModel = useCallback(() => hasPermission('chat.change_model'), [hasPermission]);
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // 便捷的权限检查方法
    canViewUsers,
    canCreateUsers,
    canEditUsers,
    canDeleteUsers,
    canViewRoles,
    canCreateRoles,
    canEditRoles,
    canDeleteRoles,
    canViewPermissions,
    canCreatePermissions,
    canEditPermissions,
    canDeletePermissions,
    canViewAPIs,
    canCreateAPIs,
    canEditAPIs,
    canDeleteAPIs,
    canRegenerateAPIs,
    canViewLogs,
    canViewUserLogs,
    canExportUserLogs,
    canViewAPILogs,
    canViewAllAPILogs,
    canExportAPILogs,
    canViewStats,
    canViewDashboardStats,
    canViewUserRoles,
    canAssignUserRoles,
    canBulkAssignRoles,
    canViewDashboard,
    canAssignRoles,
    canAssignPermissions,
    // 大模型管理权限检查
    canViewModels,
    canCreateModels,
    canEditModels,
    canDeleteModels,
    canToggleModels,
    canViewModelStats,
    canBatchOperateModels,
    // 偏好设置权限检查
    canViewPreferences,
    canEditPreferences,
    canViewPreferenceStats,
    canResetPreferences,
    // API测试权限检查
    canViewApiTest,
    canViewApiTestBalance,
    canViewApiTestModels,
    canManageApiTestKey,
    canGenerateText,
    canSetDefaultModel,
    // 充值管理权限检查
    canViewRecharge,
    // 新增：查看充值订单权限
    canViewRechargeOrders,
    // 聊天功能权限检查
    canViewChat,
    canCreateChat,
    canEditChat,
    canDeleteChat,
    canSendMessage,
    canViewChatHistory,
    canSwitchChatModel,
  };
};

// 权限保护组件
interface PermissionGuardProps {
  permission?: string;
  permissions?: string[];
  requireAll?: boolean; // true: 需要所有权限, false: 需要任一权限
  fallback?: ReactNode;
  children: ReactNode;
}

export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, loading } = usePermissions();

  if (loading) {
    return <div>权限检查中...</div>;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (permissions && permissions.length > 0) {
    hasAccess = requireAll ? hasAllPermissions(permissions) : hasAnyPermission(permissions);
  } else {
    // 没有指定权限要求，默认允许访问
    hasAccess = true;
  }

  return hasAccess ? <>{children}</> : <>{fallback}</>;
};

export default PermissionContext; 